import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, message } from 'antd';
import { ReloadOutlined, SaveOutlined, WalletOutlined } from '@ant-design/icons';
import { PaymentApp, paymentService } from '../../services/paymentService';

const { TextArea } = Input;

const DEFAULT_CONFIG: Partial<PaymentApp> = {
  appCode: 'user_payment_config',
  appName: '用户支付配置',
  isEnabled: 1,
  channel: 'wechat',
  alipayAppPay: 0,
  alipayPcPay: 0,
  alipayWapPay: 0,
  alipayQrPay: 0,
  alipayBarPay: 0,
  alipayMiniPay: 0,
  wechatJsapiPay: 1,
  wechatAppPay: 0,
  wechatNativePay: 0,
  wechatWapPay: 0,
  wechatBarPay: 0,
  walletPay: 0,
  mockPay: 0,
  wechatAppId: '',
  wechatMchId: '',
  wechatSerialNo: '',
  wechatApiKey: '',
  wechatApiV3Key: '',
  wechatPrivateKey: '',
  wechatPayPublicKeyId: '',
  wechatPayPublicKey: '',
  wechatCertPath: '',
  wechatNotifyUrl: '',
  wechatRefundNotifyUrl: '',
  nearAftersaleMinutes: 0,
  payTimeoutMinutes: 0,
  refundAuditMode: 'manual_review',
  aftersaleAuditMode: 'manual_review',
  remark: '',
};

const normalizeConfig = (config?: Partial<PaymentApp>): Partial<PaymentApp> => ({
  ...DEFAULT_CONFIG,
  ...config,
  isEnabled: typeof config?.isEnabled === 'number' ? config.isEnabled : 1,
  channel: config?.channel || 'wechat',
  mockPay: Number(config?.mockPay ?? 0),
  nearAftersaleMinutes: Number(config?.nearAftersaleMinutes ?? 0),
  payTimeoutMinutes: Number(config?.payTimeoutMinutes ?? 0),
  refundAuditMode: config?.refundAuditMode || 'manual_review',
  aftersaleAuditMode: config?.aftersaleAuditMode || 'manual_review',
});

const auditModeOptions = [
  { value: 'manual_review', label: '人工审核' },
  { value: 'auto_review', label: '自动审核' },
];

const PaymentAppPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await paymentService.getConfig();
      form.setFieldsValue(normalizeConfig(data));
    } catch (error) {
      console.error('加载用户支付配置失败', error);
      message.error('加载用户支付配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSubmit = async (values: Partial<PaymentApp>) => {
    try {
      setSubmitLoading(true);
      const payload: Partial<PaymentApp> = {
        ...normalizeConfig(values),
        appCode: 'user_payment_config',
        appName: '用户支付配置',
        isEnabled: Number(values.isEnabled ?? 1),
        channel: 'wechat',
        nearAftersaleMinutes: Number(values.nearAftersaleMinutes ?? 0),
        payTimeoutMinutes: Number(values.payTimeoutMinutes ?? 0),
        wechatJsapiPay: 1,
        wechatAppPay: 0,
        wechatNativePay: 0,
        wechatWapPay: 0,
        wechatBarPay: 0,
        alipayAppPay: 0,
        alipayPcPay: 0,
        alipayWapPay: 0,
        alipayQrPay: 0,
        alipayBarPay: 0,
        alipayMiniPay: 0,
        walletPay: 0,
        mockPay: Number(values.mockPay ?? 0),
      };

      const saved = await paymentService.saveConfig(payload);
      form.setFieldsValue(normalizeConfig(saved));
      message.success('保存配置成功');
    } catch (error) {
      console.error('保存用户支付配置失败', error);
      message.error('保存用户支付配置失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Card title={<><WalletOutlined /> 用户支付配置</>} loading={loading}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={DEFAULT_CONFIG}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              label="支付开关"
              name="isEnabled"
              rules={[{ required: true, message: '请选择支付开关' }]}
            >
              <Select
                options={[
                  { value: 1, label: '开启' },
                  { value: 0, label: '关闭' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="支付渠道"
              name="channel"
              rules={[{ required: true, message: '请选择支付渠道' }]}
            >
              <Select options={[{ value: 'wechat', label: '微信支付' }]} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item label="AppID" name="wechatAppId">
              <Input placeholder="请输入微信 AppID" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="商户号" name="wechatMchId">
              <Input placeholder="请输入微信商户号" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item label="商户证书序列号" name="wechatSerialNo">
              <Input placeholder="请输入微信商户证书序列号" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="APIv3 Key" name="wechatApiV3Key">
              <Input.Password placeholder="请输入微信 APIv3 Key" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              label="临近售后时间分钟"
              name="nearAftersaleMinutes"
              tooltip="填写非负整数，单位：分钟"
              rules={[{ type: 'number', min: 0, message: '请输入大于等于 0 的分钟数' }]}
            >
              <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="请输入临近售后时间分钟" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="支付超时取消"
              name="payTimeoutMinutes"
              tooltip="填写非负整数，单位：分钟；0 表示不自动取消未支付订单"
              rules={[{ type: 'number', min: 0, message: '请输入大于等于 0 的分钟数' }]}
            >
              <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="请输入支付超时取消分钟数" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              label="支付类型"
              name="mockPay"
              rules={[{ required: true, message: '请选择支付类型' }]}
              tooltip="模拟支付会直接标记支付成功并进入后续派单流程；真实支付会调用微信支付"
            >
              <Select
                options={[
                  { value: 0, label: '真实支付' },
                  { value: 1, label: '模拟支付' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item label="申请退款" name="refundAuditMode">
              <Select options={auditModeOptions} placeholder="请选择退款审核方式" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="申请售后" name="aftersaleAuditMode">
              <Select options={auditModeOptions} placeholder="请选择售后审核方式" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="支付回调地址"
          name="wechatNotifyUrl"
          extra="示例：https://your-domain.com/api/payment/wechat/notify"
        >
          <Input placeholder="请输入微信支付回调地址" />
        </Form.Item>

        <Form.Item
          label="退款回调地址"
          name="wechatRefundNotifyUrl"
          extra="示例：https://your-domain.com/api/payment/wechat/refund-notify"
        >
          <Input placeholder="请输入微信退款回调地址" />
        </Form.Item>

        <Form.Item
          label="商户私钥"
          name="wechatPrivateKey"
          extra="填写 apiclient_key.pem 的完整内容，真实支付和真实退款都会使用该配置"
        >
          <TextArea rows={12} placeholder="请输入微信商户私钥完整内容" />
        </Form.Item>

        <Form.Item
          label="微信支付公钥ID"
          name="wechatPayPublicKeyId"
          extra="如果商户平台已启用微信支付公钥验签，请填写回调头中的 PUB_KEY_ID"
        >
          <Input placeholder="请输入微信支付公钥ID" />
        </Form.Item>

        <Form.Item
          label="微信支付公钥"
          name="wechatPayPublicKey"
          extra="如果商户平台启用了微信支付公钥模式，请填写完整公钥内容"
        >
          <TextArea rows={8} placeholder="请输入微信支付公钥完整内容" />
        </Form.Item>

        <Form.Item label="备注" name="remark">
          <TextArea rows={4} placeholder="可填写支付环境、退款审核说明、证书说明等" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitLoading}>
              保存配置
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => void loadConfig()}>
              重新加载
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default PaymentAppPage;
